import { put, all, takeLatest } from 'redux-saga/effects';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchReducerActions, createFetchAction, createSaga, fetchInitialState } from 'store/utils';
import * as patientApi from 'api/patient';
import { PartnerActions, AppActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import { genderList } from 'lib/mapper';
import moment from 'moment';

export const new_patient = createFetchAction('new_patient');
export const fetch_patient_list = createFetchAction('fetch_patient_list');
export const fetch_patient = createFetchAction('fetch_patient');
export const edit_patient = createFetchAction('edit_patient');
export const fetch_project_patients = createFetchAction('fetch_project_patients');
export const delete_patient = createFetchAction('delete_patient');
export const check_chart_number = createFetchAction('check_chart_number');

interface InitialState {
  newPatient: object | any;
  patientList: object | any;
  patient: object | any;
  editPatient: object | any;
  projectPatients: object | any;
  deletePatient: object | any;
  checkChartNumber: object | any;
  currPatient: object | any;
}

const initialState: InitialState = {
  newPatient: { ...fetchInitialState, data: null },
  patientList: { ...fetchInitialState, data: null },
  patient: { ...fetchInitialState, data: null },
  editPatient: { ...fetchInitialState, data: null },
  projectPatients: { ...fetchInitialState, data: null },
  deletePatient: { ...fetchInitialState, data: null },
  checkChartNumber: { ...fetchInitialState, data: null },
  currPatient: null,
};

const slice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    ...fetchReducerActions(new_patient, 'newPatient', {
      success(state: any, { payload }: PayloadAction<any>) {
        const { ipcRenderer } = window;

        if (!!ipcRenderer) {
          ipcRenderer.send('setPatientList', {});
        }
      },
    }),
    ...fetchReducerActions(fetch_patient_list, 'patientList'),
    ...fetchReducerActions(fetch_patient, 'patient'),
    ...fetchReducerActions(edit_patient, 'editPatient', {
      success(state: any, { payload }: PayloadAction<any>) {
        const { ipcRenderer } = window;

        if (!!ipcRenderer) {
          ipcRenderer.send('setPatientList', {});
        }
      },
    }),
    ...fetchReducerActions(fetch_project_patients, 'projectPatients', {
      success(state: any, { payload }: PayloadAction<any>) {
        state.projectPatients.data.list.forEach(
          (element: {
            [x: string]: string;
            firstName: string;
            lastName: string;
            identification: string;
            gender: string;
            birthday: string;
          }) => {
            element['listText'] = `${!!element.firstName ? element.firstName : ''} ${
              !!element.lastName ? element.lastName : ''
            }${!!element.identification ? element.identification : ''}, ${
              genderList[element.gender as keyof typeof genderList]
            }${moment(element.birthday).isValid() ? `, ${element.birthday}` : ''}${
              !!element.pms ? ', C' : ''
            }`;
            element['fullName'] = `${!!element.firstName ? element.firstName : ''}${
              !!element.lastName ? element.lastName : ''
            }${!!element.identification ? element.identification : ''}${
              !!element.pms ? ', C' : ''
            }`;
          },
        );
      },
    }),
    ...fetchReducerActions(delete_patient, 'deletePatient', {
      success(state: any, { payload }: PayloadAction<any>) {
        const { ipcRenderer } = window;

        if (!!ipcRenderer) {
          ipcRenderer.send('setPatientList', {});
        }
      },
    }),
    ...fetchReducerActions(check_chart_number, 'checkChartNumber'),
    set_current_patient: (state, { payload }) => {
      state.currPatient = payload;
    },
  },
});

export const actions: any = slice.actions;

export function* patientSaga() {
  yield all([
    takeLatest(
      actions.new_patient_request,
      createSaga(actions, 'new_patient', patientApi.newPatient),
    ),
    takeLatest(
      actions.fetch_patient_list_request,
      createSaga(actions, 'fetch_patient_list', patientApi.fetchPatientList),
    ),
    takeLatest(
      actions.fetch_patient_request,
      createSaga(actions, 'fetch_patient', patientApi.fetchPatient),
    ),
    takeLatest(
      actions.edit_patient_request,
      createSaga(actions, 'edit_patient', patientApi.editPatient),
    ),
    takeLatest(
      actions.fetch_project_patients_request,
      createSaga(actions, 'fetch_project_patients', patientApi.fetchProjectPatients, {
        isApiLoading: false,
      }),
    ),
    takeLatest(
      actions.delete_patient_request,
      createSaga(actions, 'delete_patient', patientApi.deletePatient, { isAlertfailure: false }),
    ),
    takeLatest(
      actions.check_chart_number_request,
      createSaga(actions, 'check_chart_number', patientApi.checkChartNumber, {
        isAlertfailure: false,
      }),
    ),
  ]);
}

export default slice.reducer;
